import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { toast } from 'sonner';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Trash2,
  TrendingUp,
  Clock,
  Calendar,
  GraduationCap,
  UserPlus,
  Mail,
  Award,
  Medal,
  Target,
  Zap,
  AlertCircle,
  BarChart3,
  AtSign,
  Megaphone,
  Wrench,
  Sparkles,
  CreditCard,
  CheckCircle,
  Loader2,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for notification types
const NOTIFICATION_ICONS = {
  TrendingUp: TrendingUp,
  Clock: Clock,
  Calendar: Calendar,
  GraduationCap: GraduationCap,
  UserPlus: UserPlus,
  Mail: Mail,
  Award: Award,
  Medal: Medal,
  Target: Target,
  Zap: Zap,
  AlertCircle: AlertCircle,
  BarChart3: BarChart3,
  AtSign: AtSign,
  Megaphone: Megaphone,
  Wrench: Wrench,
  Sparkles: Sparkles,
  CreditCard: CreditCard,
  CheckCircle: CheckCircle,
  Bell: Bell,
};

// Category colors
const CATEGORY_COLORS = {
  league: 'text-blue-400 bg-blue-500/20',
  achievement: 'text-amber-400 bg-amber-500/20',
  portfolio: 'text-emerald-400 bg-emerald-500/20',
  social: 'text-purple-400 bg-purple-500/20',
  system: 'text-slate-400 bg-slate-500/20',
  pro: 'text-rose-400 bg-rose-500/20',
};

// Priority indicator
const PriorityDot = ({ priority }) => {
  if (priority === 'urgent') {
    return <div className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />;
  }
  if (priority === 'high') {
    return <div className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" />;
  }
  return null;
};

// Single notification item
const NotificationItem = ({ notification, onRead, onDismiss, onAction }) => {
  const IconComponent = NOTIFICATION_ICONS[notification.icon] || Bell;
  const categoryColor = CATEGORY_COLORS[notification.category] || CATEGORY_COLORS.system;
  
  return (
    <div 
      className={`relative p-3 rounded-lg transition-colors cursor-pointer ${
        notification.is_read 
          ? 'bg-slate-800/30 hover:bg-slate-800/50' 
          : 'bg-slate-800/70 hover:bg-slate-700/70 border-l-2 border-emerald-500'
      }`}
      onClick={() => {
        if (!notification.is_read) {
          onRead(notification.id);
        }
        if (notification.action_url) {
          onAction(notification.action_url);
        }
      }}
      data-testid={`notification-${notification.id}`}
    >
      <PriorityDot priority={notification.priority} />
      
      <div className="flex gap-3">
        <div className={`p-2 rounded-lg flex-shrink-0 ${categoryColor}`}>
          <IconComponent className="h-4 w-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-sm font-medium truncate ${notification.is_read ? 'text-slate-300' : 'text-white'}`}>
              {notification.title}
            </h4>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(notification.id);
              }}
              className="text-slate-500 hover:text-slate-300 p-1 rounded"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          
          <p className={`text-xs mt-0.5 line-clamp-2 ${notification.is_read ? 'text-slate-500' : 'text-slate-400'}`}>
            {notification.message}
          </p>
          
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate-500">{notification.time_ago}</span>
            
            {notification.action_label && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs text-emerald-400 hover:text-emerald-300 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!notification.is_read) {
                    onRead(notification.id);
                  }
                  onAction(notification.action_url);
                }}
              >
                {notification.action_label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main NotificationBell component
const NotificationBell = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pollingRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async (showLoading = true) => {
    if (!token) return;
    
    try {
      if (showLoading) setLoading(true);
      
      const res = await fetch(`${API_URL}/api/notifications?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
        setHasMore(data.has_more || false);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch unread count only (for polling)
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      // Silently fail for polling
    }
  }, [token]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!token) return;
    
    pollingRef.current = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [token, fetchUnreadCount]);

  // Refetch when popover opens
  useEffect(() => {
    if (open) {
      fetchNotifications(false);
    }
  }, [open, fetchNotifications]);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify([notificationId])
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const wasUnread = notifications.find(n => n.id === notificationId && !n.is_read);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      toast.error('Failed to dismiss notification');
    }
  };

  // Clear all notifications
  const clearAll = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/clear-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (err) {
      toast.error('Failed to clear notifications');
    }
  };

  // Handle action URL
  const handleAction = (url) => {
    setOpen(false);
    if (url) {
      navigate(url);
    }
  };

  if (!token) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative p-2"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5 text-slate-400" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 bg-emerald-500 text-white text-xs flex items-center justify-center"
              data-testid="unread-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-80 sm:w-96 p-0 bg-slate-900 border-slate-700"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <h3 className="font-semibold text-white">Notifications</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-slate-400 hover:text-white"
                onClick={markAllAsRead}
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-slate-400 hover:text-red-400"
                onClick={clearAll}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Notification list */}
        <ScrollArea className="max-h-[60vh]">
          <div className="p-2 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <BellOff className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1 text-slate-500">
                  We'll notify you about leagues, scores, and more
                </p>
              </div>
            ) : (
              notifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={markAsRead}
                  onDismiss={dismissNotification}
                  onAction={handleAction}
                />
              ))
            )}
          </div>
        </ScrollArea>
        
        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-700">
            <Button
              variant="ghost"
              className="w-full h-8 text-xs text-slate-400 hover:text-white"
              onClick={() => {
                setOpen(false);
                navigate('/settings?tab=notifications');
              }}
            >
              Notification Settings
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
